// Reviewer evidence for /evidence: each judging criterion mapped to the demo step that shows it, the
// contracts involved, the source file or commit, its limitation, and whether the claim is measured,
// modeled or planned. Addresses and SHAs: docs/deployment.md in the product repository (broadcast of
// 2026-09-23 from 98f8b45) and the Deployed table on the home page (src/lib/chain.ts).
import { M_CONFIG, M_HOOK, M_POOL_ID, M_TIMELOCK, MAINNET, NVDA, T_CONFIG, T_HOOK, T_POOL, TESTNET, VERIFIER, type ChainId } from './chain';

export type EvidenceLabel = 'measured' | 'modeled' | 'planned' | 'external';

export const REPO = 'https://github.com/devdotbo/bellguard';
export const repoFile = (path: string, lines?: string): string => `${REPO}/blob/main/${path}${lines ? `#L${lines.replace('-', '-L')}` : ''}`;
export const repoCommit = (sha: string): string => `${REPO}/commit/${sha}`;

/** The product repository commit the 2026-09-23 mainnet and testnet v2 broadcasts ran from. */
export const DEPLOY_SHA = '98f8b45';

export interface EvidenceContract { name: string; chainId: ChainId; addr: string; kind?: 'address' | 'tx' | 'pool' }
export interface EvidenceSource { text: string; href: string }

export interface EvidenceRow {
  criterion: string;
  /** Which rubric names it: the platform's four criteria, or the terms and conditions. */
  rubric: 'platform' | 'terms';
  claim: string;
  demo: string;
  contracts: EvidenceContract[];
  sources: EvidenceSource[];
  limitation: string;
  labels: EvidenceLabel[];
}

const MAIN_POOL_INIT_TX = '0xe325776d3caacdf069d8789b30891d04ac86b39ec6b3825ca62c9efb7d630ebf';
const TEST_POOL_INIT_TX = '0xd5c29214d8e4c69dd043a32da0f5dcfe3508fce914670a7eab92f4817aca1895';
const TEST_SWAP_FRESH_TX = '0x757a80a9fb5526090e9e7870be7a966915441afec4738726aba2e3d897728436';
const TEST_SWAP_STALE_TX = '0xd1fa780f612ded4125aac64ca68cdb0919c954449209cec72d8ca8d64e9f9e79';

export const EVIDENCE: readonly EvidenceRow[] = [
  {
    criterion: 'Smart Contract Quality',
    rubric: 'platform',
    claim: 'StockVerifier (ownerless, no setter, no upgrade path), BellHook and BellConfig are deployed on Robinhood Chain mainnet with Sourcify exact matches. BellConfig parameters are governed by a TimelockController with a 24 hour delay (the deployer holds the proposer, executor and canceller roles, not the admin role); the hook refuses a pool unless exactly one side passes the verifier and the other is USDG or WETH with a configured feed.',
    demo: 'Home page, Contracts and Deployed: each address links to its explorer page and the table reads its code from the chain on load. Impostor refusal: PoolManager.initialize reverts in beforeInitialize.',
    contracts: [
      { name: 'StockVerifier', chainId: MAINNET, addr: VERIFIER },
      { name: 'BellHook', chainId: MAINNET, addr: M_HOOK },
      { name: 'BellConfig', chainId: MAINNET, addr: M_CONFIG },
      { name: 'TimelockController (86,400 s)', chainId: MAINNET, addr: M_TIMELOCK },
    ],
    sources: [
      { text: 'src/StockVerifier.sol', href: repoFile('src/StockVerifier.sol') },
      { text: 'src/BellHook.sol:186-200 (admission)', href: repoFile('src/BellHook.sol', '186-200') },
      { text: 'src/BellConfig.sol', href: repoFile('src/BellConfig.sol') },
      { text: `deploy commit ${DEPLOY_SHA}`, href: repoCommit(DEPLOY_SHA) },
      { text: 'docs/deployment.md (gate evidence, refusals)', href: repoFile('docs/deployment.md') },
      { text: 'Blockscout: BellHook, verified source and ABI', href: 'https://robinhoodchain.blockscout.com/address/0xeF6c47Dc2999C19CeC6a06982283dd9dBD7cE5D0' },
      { text: 'Blockscout: StockVerifier, verified source and ABI', href: 'https://robinhoodchain.blockscout.com/address/0x574cB84717b8aF7Df5F40a8344D783728317114f' },
    ],
    limitation: 'Not audited. All eight contracts (four on mainnet 4663, four on testnet 46630) are verified on Blockscout with source and ABI (Blockscout links for BellHook and StockVerifier under Source); on Sourcify both hooks have a runtime match without a creation match. Known open issues: an honest stale swap into thin liquidity can revert, splitting a token across pools can avoid the float guard, and a future pool can be initialised at an arbitrary price.',
    labels: ['measured'],
  },
  {
    criterion: 'Product-Market Fit',
    rubric: 'platform',
    claim: "The decision the product serves: check a Stock Token's origin and inspect the fee policy before committing USDG. The mainnet pool is NVDA/USDG on BellHook. The issuer's factory exposes an official lookup and Fletch offers an identity API; Bellguard's distinct part is a contract-callable, ownerless, factory-independent recomputation plus pool admission.",
    demo: "Home page: Verify a token (the verifier answer for any address) and Fee quote (the deployed hook's fee for a pool, amount and direction).",
    contracts: [
      { name: 'NVDA/USDG pool id', chainId: MAINNET, addr: M_POOL_ID, kind: 'pool' },
      { name: 'pool initialisation', chainId: MAINNET, addr: MAIN_POOL_INIT_TX, kind: 'tx' },
      { name: 'NVDA (official Stock Token)', chainId: MAINNET, addr: NVDA },
    ],
    sources: [
      { text: 'docs/deployment.md (mainnet v2 broadcast)', href: repoFile('docs/deployment.md') },
      { text: 'the tools on this site', href: '/#verify' },
    ],
    limitation: 'The mainnet pool is initialised with no liquidity. No third party calls the verifier yet. Integrations with protocols and LPs are planned, not built.',
    labels: ['measured', 'planned'],
  },
  {
    criterion: 'Innovation and Creativity',
    rubric: 'platform',
    claim: 'Two parts that need each other: a verifier that recomputes the issuer factory CREATE2 address instead of trusting a list, and a fee that follows the measured gap between the pool and the frozen Chainlink feed rather than the calendar. The fee returns to the pool tier fee once the feed prints.',
    demo: 'Testnet demo pool: a swap with a fresh feed charged 30 bps (the tier fee); a swap with a stale feed charged 178.5 bps, priced on the 322 bps post-swap gap.',
    contracts: [
      { name: 'BellHook (testnet)', chainId: TESTNET, addr: T_HOOK },
      { name: 'swap, fresh feed, 30 bps', chainId: TESTNET, addr: TEST_SWAP_FRESH_TX, kind: 'tx' },
      { name: 'swap, stale feed, 178.5 bps', chainId: TESTNET, addr: TEST_SWAP_STALE_TX, kind: 'tx' },
    ],
    sources: [
      { text: 'docs/fee-model.md', href: repoFile('docs/fee-model.md') },
      { text: 'docs/demo.md (runbook)', href: repoFile('docs/demo.md') },
    ],
    limitation: 'The stale fee is symmetric: it cannot tell a gap-closing trade from a gap-widening one. The float surcharge can stay on after a fresh print. An opening-bell regime (v3) is planned, not built.',
    labels: ['measured', 'planned'],
  },
  {
    criterion: 'Real Problem Solving',
    rubric: 'platform',
    claim: 'Impostor tokens: Bitquery (July 2026) counted 361 GME contracts, 213 M USD of impostor volume, 64,394 wallets (addresses, not people) and the median impostor holder down 52.4 percent (Bitquery, addresses not people). Hidden hook fees: Bitquery (2026-09-08) found one dominant hook that records 0 percent and takes a median 1 percent in a one-day sample. Fee results at unchanged flow: 2.01x over the weekend and 4.17x in the final hour on 2026-09-14; 1.62x and 1.35x on 2026-09-21; weekday calibration 1.003x. Weekend markout per unit of volume was lower than on weekday controls.',
    demo: 'Home page FAQ, "The impostor problem" and "What happened at the last reopen?"; Fee transparency compares the fee quoted with the fee charged per swap.',
    contracts: [
      { name: 'StockVerifier', chainId: MAINNET, addr: VERIFIER },
    ],
    sources: [
      { text: 'docs/analysis/2026-09-reopen/verify/tier-recompute-2026-09-23.md', href: repoFile('docs/analysis/2026-09-reopen/verify/tier-recompute-2026-09-23.md') },
    ],
    limitation: 'The fee ratios are modeled: the marginal curve applied to unchanged historical trades, leaving out the post-swap gap projection, the float surcharge, swaps the hook would revert and any trader response. They are not realised LP profit, and where the fee rises they are an upper bound. The Bitquery figures are third-party measurements.',
    labels: ['external', 'modeled'],
  },
  {
    criterion: 'Use of Arbitrum technology',
    rubric: 'terms',
    claim: 'Everything runs on Robinhood Chain, an Arbitrum Orbit chain: the verifier and hook v2 on mainnet 4663, the full demo stack on testnet 46630, against the chain\'s Uniswap v4 PoolManager and USDG.',
    demo: 'Home page Deployed table (both chains); the hook card reads the testnet BellHook and BellConfig live.',
    contracts: [
      { name: 'BellConfig (testnet)', chainId: TESTNET, addr: T_CONFIG },
      { name: 'TSLA/mUSDG pool id (testnet)', chainId: TESTNET, addr: T_POOL.id, kind: 'pool' },
    ],
    sources: [
      { text: 'docs/deployments/mainnet-4663-v2.json', href: repoFile('docs/deployments/mainnet-4663-v2.json') },
      { text: 'docs/deployments/testnet-46630-v2.json', href: repoFile('docs/deployments/testnet-46630-v2.json') },
    ],
    limitation: 'The testnet feed is a mock set by hand (Chainlink publishes no testnet Stock Token feeds) and the testnet quote token is MockUSDG, because testnet USDG has no faucet.',
    labels: ['measured'],
  },
  {
    criterion: 'Presentation quality',
    rubric: 'terms',
    claim: 'This site states each figure with its date and source and labels it measured, modeled, external or planned; the tools on the home page call the deployed contracts through a public API: /verify and /quote are live, and /hook/fees returns an empty state until the indexer schema switch.',
    demo: 'bellguard.xyz: hero, Verify a token, Fee quote, Fee transparency, FAQ, and this page. Demo video: planned.',
    contracts: [],
    sources: [
      { text: 'API: api.bellguard.xyz/api/data/verify', href: 'https://api.bellguard.xyz/api/data/verify/0x574cB84717b8aF7Df5F40a8344D783728317114f' },
      { text: 'API: api.bellguard.xyz/api/data/hook/fees', href: 'https://api.bellguard.xyz/api/data/hook/fees?chainId=46630' },
      { text: 'docs/demo.md (narration)', href: repoFile('docs/demo.md') },
    ],
    limitation: 'Figures last verified on 23 September 2026; explorer verification and API status checked on 24 September 2026. The demo video is not recorded yet.',
    labels: ['measured', 'planned'],
  },
];

export const contractHref = (c: EvidenceContract): string => {
  const base = c.chainId === MAINNET ? 'https://robinhoodchain.blockscout.com' : 'https://explorer.testnet.chain.robinhood.com';
  if (c.kind === 'tx') return `${base}/tx/${c.addr}`;
  if (c.kind === 'pool') return `${base}/tx/${c.chainId === MAINNET ? MAIN_POOL_INIT_TX : TEST_POOL_INIT_TX}`;
  return `${base}/address/${c.addr}`;
};
